import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import {
  CircularProgress,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Input,
  MenuItem,
  Select,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import CheckIcon from "@mui/icons-material/Check";
import FormHelperText from "@mui/material/FormHelperText";
import {
  checkEmailExist,
  checkLoginExist,
  searchService,
  userService,
} from "../../services";
import {
  Add,
  Close,
  Delete,
  Save,
  Visibility,
  VisibilityOff,
  VpnKey,
} from "@mui/icons-material";
import Button from "@mui/material/Button";
import SearchForm from "./SearchForm";

const PREFIX = "UserForm";

const classes = {
  root: `${PREFIX}-root`,
  paper: `${PREFIX}-paper`,
  form: `${PREFIX}-form`,
  marginBottom: `${PREFIX}-marginBottom`,
  grid: `${PREFIX}-grid`,
  gridList: `${PREFIX}-gridList`,
  tileWidth: `${PREFIX}-tileWidth`,
  title: `${PREFIX}-title`,
  titleBar: `${PREFIX}-titleBar`,
  buttons: `${PREFIX}-buttons`,
  selectMenuStyle: `${PREFIX}-selectMenuStyle`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.root}`]: {
    height: "100vh",
  },

  [`& .${classes.paper}`]: {
    padding: theme.spacing(2, 3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    margin: "0 auto",
  },

  [`& .${classes.form}`]: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
    position: "relative",
    top: 0,
  },

  [`& .${classes.marginBottom}`]: {
    width: "100%",
    marginBottom: "1.5rem",
  },

  [`& .${classes.grid}`]: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    padding: "0.5rem",
    height: "200px",
    overflow: "auto",
  },

  [`& .${classes.gridList}`]: {
    flexWrap: "nowrap",
    overflow: "auto",
    display: "flex",
    height: "182px",
    "&::-webkit-scrollbar": {
      width: "5px",
      height: "5px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "3px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "rgb(181 154 79 / 58%)",
      borderRadius: "10px",
    },
    // Promote the list into his own layer on Chrome. This cost memory but helps keeping high FPS.
    transform: "translateZ(0)",
  },

  [`& .${classes.tileWidth}`]: {
    // width: 'inherit',
    // maxWidth: '270px',
    height: "170px !important",
  },

  [`& .${classes.title}`]: {
    color: theme.palette.primary.light,
    fontSize: "0.7rem",
  },

  [`& .${classes.titleBar}`]: {
    background:
      "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)",
    "& > div": {
      marginLeft: "5px",
    },
  },

  [`& .${classes.buttons}`]: {
    display: "flex",
    justifyContent: "space-evenly",
    marginTop: "0.5rem",
  },

  [`& .${classes.selectMenuStyle}`]: {
    backgroundColor: "#424242",
  },
}));

function useDebounce(value, delay) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );

  return debouncedValue;
}

export default function UserForm(props) {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });
  const { t } = useTranslation();

  const debouncedSearchEmail = useDebounce(watch("email"), 300);
  const debouncedSearchLogin = useDebounce(watch("login"), 300);
  const [isLoginUsed, setIsLoginUsed] = useState(null);
  const [isLoginSearching, setIsLoginSearching] = useState(false);
  const [isEmailUsed, setIsEmailUsed] = useState(null);
  const [isEmailSearching, setIsEmailSearching] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [updatePassword, setUpdatePassword] = useState(false);
  const [myDesigns, setMyDesigns] = useState([]);
  const [searchResult, setSearchResult] = useState([]);
  const [role, setRole] = useState("USER");

  useEffect(() => {
    if (props.user) {
      userService.getUserFrames(props.user.id).then((res) => setMyDesigns(res));
      setRole(props.user.role);
    }
  }, []);

  useEffect(() => {
    if (
      debouncedSearchEmail &&
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        watch("email")
      ) &&
      debouncedSearchEmail !== props.user?.email
    ) {
      setIsEmailSearching(true);
      checkEmailExist(debouncedSearchEmail).then((results) => {
        setIsEmailSearching(false);
        // Filter out results
        setIsEmailUsed(results);
      });
    }
  }, [debouncedSearchEmail]);

  useEffect(() => {
    if (debouncedSearchLogin && debouncedSearchLogin !== props.user?.login) {
      setIsLoginSearching(true);
      checkLoginExist(debouncedSearchLogin).then((results) => {
        setIsLoginSearching(false);
        // Filter out results
        setIsLoginUsed(results);
      });
    }
  }, [debouncedSearchLogin]);

  function onSubmit(data) {
    let tmp = { ...data, role };
    if (props.user) tmp.id = props.user.id;
    props.save(tmp);
  }

  function savePassword() {
    setUpdatePassword(false);
    props.savePassword(watch("newPassword"), props.user.id);
  }

  async function search(value) {
    if (value && value.length > 0) {
      const results = await searchService.searchMyDesigns(value);
      setSearchResult(
        results.filter((r) => !myDesigns.find((md) => md.id === r.id))
      );
    }
  }

  async function addFrame(myDesign) {
    await userService.addUserFrame(props.user.id, myDesign.id);
    setMyDesigns((oldValue) => [...oldValue, myDesign]);
    setSearchResult((oldValue) =>
      oldValue.filter((md) => md.id !== myDesign.id)
    );
  }

  async function deleteFrame(myDesign) {
    await userService.deleteUserFrame(props.user.id, myDesign.id);
    setMyDesigns((oldValue) => oldValue.filter((md) => md.id !== myDesign.id));
  }

  return (
    <Root>
      <div className={classes.paper}>
        <h3>{t("users.dataUser")}</h3>
        <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
          <Input
            type="hidden"
            name="id"
            id="id"
            defaultValue={props.user?.id || -1}
            // inputRef={register()}
          />
          <FormControl variant="outlined" className={classes.marginBottom}>
            <InputLabel htmlFor="email">
              {t("authForms.signin.email")}
            </InputLabel>
            <OutlinedInput
              required
              fullWidth
              name="email"
              id="email"
              type="text"
              label={t("authForms.signin.email")}
              error={errors.hasOwnProperty("email") || isEmailUsed}
              autoComplete="email"
              {...register("email", {
                validate: (value) =>
                  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
                    value
                  )
                    ? null
                    : t("account.updateEmailForm.wrongEmailFormat"),
              })}
              endAdornment={
                (isEmailUsed !== null || isEmailSearching) && (
                  <InputAdornment position="end">
                    <IconButton aria-label="email availability" size="large">
                      {isEmailSearching ? (
                        <CircularProgress />
                      ) : isEmailUsed ? (
                        <ClearIcon />
                      ) : (
                        <CheckIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                )
              }
              defaultValue={props.user?.email || ""}
              disabled={updatePassword}
            />
            {isEmailUsed && (
              <FormHelperText id="email" error>
                {t("authForms.signin.errorMessages.email")}
              </FormHelperText>
            )}
          </FormControl>
          <FormControl variant="outlined" className={classes.marginBottom}>
            <InputLabel htmlFor="login">
              {t("authForms.signin.username")}
            </InputLabel>
            <OutlinedInput
              required
              fullWidth
              name="login"
              id="login"
              type="text"
              label={t("authForms.signin.username")}
              error={errors.hasOwnProperty("login") || isEmailUsed}
              autoComplete="login"
              {...register("login", { required: true })}
              endAdornment={
                (isEmailUsed !== null || isEmailSearching) && (
                  <InputAdornment position="end">
                    <IconButton aria-label="login availability" size="large">
                      {isLoginSearching ? (
                        <CircularProgress />
                      ) : isLoginUsed ? (
                        <ClearIcon />
                      ) : (
                        <CheckIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                )
              }
              defaultValue={props.user?.login || ""}
              disabled={updatePassword}
            />
            {isLoginUsed && (
              <FormHelperText id="login" error>
                {t("authForms.signin.errorMessages.login")}
              </FormHelperText>
            )}
          </FormControl>
          {!props.user && (
            <FormControl variant="outlined" className={classes.marginBottom}>
              <InputLabel htmlFor="password">
                {t("authForms.signin.password")}
              </InputLabel>
              <OutlinedInput
                required
                fullWidth
                name="password"
                id="password"
                type={isPasswordVisible ? "text" : "password"}
                label={t("authForms.signin.password")}
                error={errors.hasOwnProperty("password")}
                {...register("password", { required: true })}
                autoComplete="current-password"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="view password"
                      onClick={() => setIsPasswordVisible((old) => !old)}
                      size="large"
                    >
                      {isPasswordVisible ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
          )}
          {props.user && (
            <FormControl
              variant="outlined"
              className={classes.marginBottom}
              fullWidth
            >
              <div className={classes.marginBottom}>
                {updatePassword ? (
                  <div>
                    <InputLabel htmlFor="password">
                      {t("authForms.signin.password")}
                    </InputLabel>
                    <OutlinedInput
                      required
                      fullWidth
                      name="newPassword"
                      id="newPassword"
                      type={isPasswordVisible ? "text" : "password"}
                      label={t("authForms.signin.password")}
                      error={errors.hasOwnProperty("newPassword")}
                      {...register("newPassword", { required: true })}
                      autoComplete="current-password"
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="view newPassword"
                            onClick={() => setIsPasswordVisible((old) => !old)}
                            size="large"
                          >
                            {isPasswordVisible ? (
                              <Visibility />
                            ) : (
                              <VisibilityOff />
                            )}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                    <IconButton
                      variant="contained"
                      onClick={savePassword}
                      size="large"
                    >
                      <Save />
                    </IconButton>
                    <IconButton
                      variant="contained"
                      onClick={() => setUpdatePassword(false)}
                      size="large"
                    >
                      <Close />
                    </IconButton>
                  </div>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setUpdatePassword(true)}
                  >
                    <VpnKey />
                    <span>Mot de passe</span>
                  </Button>
                )}
              </div>
            </FormControl>
          )}
          <FormControl
            variant="outlined"
            className={classes.formControl}
            fullWidth
          >
            <InputLabel id="role">{t("users.role")}</InputLabel>
            <Select
              {...props}
              disabled={updatePassword}
              value={role}
              label={t("users.role")}
              labelId="role"
              error={errors.hasOwnProperty("role")}
              onChange={(e) => setRole(e.target.value)}
              required
              MenuProps={{ classes: { paper: classes.selectMenuStyle } }}
            >
              <MenuItem value="USER" id="user">
                {t("users.user")}
              </MenuItem>
              <MenuItem value="ADMIN" id="admin">
                {t("users.admin")}
              </MenuItem>
            </Select>
          </FormControl>
          <div className={classes.buttons}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={
                Object.keys(errors).length > 0 ||
                isEmailUsed ||
                isLoginUsed ||
                updatePassword
              }
            >
              <Save />
              {t("users.save")}
            </Button>
            <Button
              variant="contained"
              onClick={props.cancel}
              disabled={updatePassword}
            >
              <Close />
              {t("users.reject")}
            </Button>
          </div>
        </form>
        {props.user && (
          <div className={classes.marginBottom}>
            <h3>{t("users.dataMydesign")}</h3>
            {myDesigns && myDesigns.length > 0 && (
              <div className={classes.grid}>
                <div className={classes.gridList} spacing={12}>
                  {myDesigns.map((tile) => {
                    return (
                      <Root key={tile.id}>
                        <div style={{ width: "150px", marginRight: "0.5rem" }}>
                          <img
                            src={tile.imageUrl}
                            alt={tile.label}
                            className={classes.tileWidth}
                          />
                          <ImageListItemBar
                            title={tile.label}
                            classes={{
                              root: classes.titleBar,
                              title: classes.title,
                            }}
                            actionIcon={
                              <IconButton
                                onClick={() => deleteFrame(tile)}
                                size="small"
                              >
                                <Delete />
                              </IconButton>
                            }
                          />
                        </div>
                      </Root>
                    );
                  })}
                </div>
              </div>
            )}
            <SearchForm
              searchValue={""}
              search={search}
              searchText={t("users.addMyDesign")}
            />
            {searchResult && searchResult.length > 0 && (
              <div className={classes.grid}>
                <div className={classes.gridList} spacing={12}>
                  {searchResult.map((tile) => {
                    return (
                      <Root key={tile.id} style={{ marginRight: "0.5rem" }}>
                        <ImageListItem key={tile.id} style={{ width: "180px" }}>
                          <img
                            src={tile.imageUrl}
                            alt={tile.label}
                            className={classes.tileWidth}
                          />
                          <ImageListItemBar
                            title={tile.label}
                            classes={{
                              root: classes.titleBar,
                              title: classes.title,
                            }}
                            actionIcon={
                              <IconButton
                                variant="contained"
                                color="primary"
                                aria-label={`add ${tile.label}`}
                                onClick={() => addFrame(tile)}
                                size="small"
                              >
                                <Add className={classes.title} />
                              </IconButton>
                            }
                          />
                        </ImageListItem>
                      </Root>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Root>
  );
}
